import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ContactForm: React.FC = () => {
  const { t } = useTranslation();

  const contactMethods = [
    {
      type: 'email',
      title: 'Email',
      value: 'info@tamerin.tech',
      href: 'mailto:info@tamerin.tech?subject=AI%20Art%20Request',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      description: t('contact.emailDescription'),
      buttonText: t('contact.sendEmail')
    },
    {
      type: 'whatsapp',
      title: 'WhatsApp',
      value: '+49-1590-7614478',
      href: 'https://wa.me/4915907614478?text=Hi%2C%20ich%20m%C3%B6chte%20gerne%20ein%20individuelles%20AI%20Art%20Werk%20bestellen.',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
      ),
      description: t('contact.whatsappDescription'),
      buttonText: t('contact.openWhatsApp')
    }
  ];

  return (
    <section id="contact" className="py-24 bg-dark-secondary relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"></div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Elegant separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '120px', opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/50 mx-auto mb-8"
          />

          <h2 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-light text-cream mb-6 tracking-wide">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-cream-dark max-w-3xl mx-auto font-light leading-relaxed">
            {t('contact.subtitle')}
          </p>

          {/* Bottom separator */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: '80px', opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
            className="h-px bg-gold/30 mx-auto mt-8"
          />
        </motion.div>

        {/* Contact Methods */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {contactMethods.map((method, index) => (
            <motion.div
              key={method.type}
              className="group bg-dark-surface/50 border border-dark-border/30 backdrop-blur-sm p-6 sm:p-8 lg:p-10 hover:border-gold/30 hover:bg-dark-surface/70 transition-all duration-500"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {/* Icon */}
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 border border-dark-border/30 bg-dark-primary/50 flex items-center justify-center group-hover:border-gold/50 group-hover:bg-gold/5 transition-all duration-500">
                  <div className="text-gold group-hover:text-gold/90 transition-colors duration-300">
                    {method.icon}
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="font-playfair text-2xl text-cream mb-1 group-hover:text-gold/90 transition-colors duration-300">
                    {method.title}
                  </h3>
                  <p className="text-gold/80 font-light tracking-wider text-sm">
                    {method.value}
                  </p>
                </div>
              </div>

              {/* Description */}
              <p className="text-cream-dark font-light leading-relaxed mb-8">
                {method.description}
              </p>

              {/* CTA Button */}
              <a
                href={method.href}
                target={method.type === 'whatsapp' ? '_blank' : undefined}
                rel={method.type === 'whatsapp' ? 'noopener noreferrer' : undefined}
                className="group/btn inline-block w-full text-center px-8 py-4 border border-gold/30 hover:border-gold hover:bg-gold/5 transition-all duration-500"
              >
                <span className="text-cream font-light tracking-widest uppercase text-sm group-hover/btn:text-gold/90 transition-colors duration-300">
                  {method.buttonText}
                </span>
                <svg
                  className="inline-block ml-3 w-4 h-4 group-hover/btn:translate-x-2 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          className="text-center mt-16 p-8 bg-dark-surface/30 border border-dark-border/20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="w-12 h-px bg-gold/50 mx-auto mb-6"></div>
          <p className="text-cream-dark font-light leading-relaxed max-w-2xl mx-auto">
            {t('contact.additionalInfo')}
          </p>
        </motion.div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-4 w-24 h-24 bg-gold/5 rounded-full filter blur-xl animate-float"></div>
        <div className="absolute bottom-20 left-4 w-32 h-32 bg-gold/5 rounded-full filter blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
      </div>
    </section>
  );
};

export default ContactForm;